import i18n, { i18n as I18n, InitOptions } from "i18next";
import { initReactI18next } from "react-i18next";
import Backend from "i18next-http-backend";
import EnTranslation from "./translations/en/translation.json";
import DeTranslation from "./translations/de/translation.json";
import NlTranslation from "./translations/nl/translation.json";
import { cellTranslationVariables } from "@topi/shared/cell/cell-translation-variables";
import { CATALOGS } from "./catalogs";
import {
  brandBaseOf,
  brandBases,
  FALLBACK,
  formatLocaleFor,
  getFullLocale,
  SUPPORTED_LANGUAGES,
} from "./locale";

export {
  detectLocales,
  formatLocaleFor,
  getFullLocale,
  languageFor,
  switchToBrandLanguage,
} from "./locale";

interface Language {
  nativeName: string;
}

interface SupportedLanguages {
  [index: string]: Language;
}

export const lngs: SupportedLanguages = {
  en: { nativeName: "English" },
  de: { nativeName: "Deutsch" },
};

export const resources = {
  en: { EnTranslation },
  de: { DeTranslation },
  nl: { NlTranslation },
} as const;

let isInitialized = false;

i18n.on("languageChanged", () => {
  document.body.dir = i18n.dir();
});

type Format = (value: unknown) => string;

// i18next's built-in formats, through its public formatter API, so they keep
// working across upgrades. `list` is left out: ES2020 has no `Intl.ListFormat`.
const builtInFormats: Record<
  string,
  (locale: string | undefined, options: Record<string, unknown>) => Format
> = {
  number: (locale, options) => {
    const intl = new Intl.NumberFormat(locale, options);
    return (value) => intl.format(value as number);
  },
  currency: (locale, options) => {
    const intl = new Intl.NumberFormat(locale, {
      ...options,
      style: "currency",
    });
    return (value) => intl.format(value as number);
  },
  datetime: (locale, options) => {
    const intl = new Intl.DateTimeFormat(locale, options);
    return (value) => intl.format(value as Date);
  },
  relativetime: (locale, options) => {
    const intl = new Intl.RelativeTimeFormat(locale, options);
    const unit = (options.range as Intl.RelativeTimeFormatUnit) ?? "day";
    return (value) => intl.format(value as number, unit);
  },
};

/**
 * Makes i18next's `{{val, datetime}}`-style formats accept a brand language,
 * by registering the built-in ones again with a locale Intl accepts.
 */
export const formatInLocale = (instance: I18n): void => {
  const { formatter } = instance.services;
  if (!formatter) {
    return;
  }
  Object.entries(builtInFormats).forEach(([name, build]) => {
    formatter.addCached(
      name,
      (lng, options): Format => build(lng && formatLocaleFor(lng), options)
    );
  });
};

// Region first, then language: "de-DE" and "de_DE" give ["de-DE", "de"].
const regionalChain = (locale: string): string[] => {
  const [rawLanguage, region] = locale.split(/[-_]/);
  const language = rawLanguage.toLowerCase();
  return region?.length === 2
    ? [`${language}-${region.toUpperCase()}`, language]
    : [language];
};

/**
 * Where a key goes when `code` has no value for it. Brand catalogs hold only
 * the keys the brand overrides, and nest like `en-US` over `en`: a brand
 * language falls back to the brand on its broader bases in the same
 * language, then to the topi chain in that language. On `en-US`,
 * `en_us_peac_us_inc` falls back to `en_peac_us_inc`, then `en`. Languages
 * without a catalog are left out, so they aren't requested. i18next calls
 * this without a code for an unsupported language.
 */
export const fallbackFor = (locale: string, code?: string): string[] => {
  if (!code) {
    return [FALLBACK];
  }
  const base = brandBaseOf(code, locale);
  let chain = regionalChain(code);
  if (base) {
    const brand = code.slice(base.length + 1);
    const language = base.split("_")[0];
    const bases = brandBases(locale);
    const brandChain = bases
      .slice(bases.indexOf(base) + 1)
      .filter((broader) => broader.split("_")[0] === language)
      .map((broader) => `${broader}_${brand}`);
    // A brand built on `en` for an `es-US` browser falls back to topi's `en`.
    const sameLanguage = bases[0].split("_")[0] === language;
    chain = [...brandChain, ...regionalChain(sameLanguage ? locale : language)];
  }
  return [
    ...new Set([...chain.filter((c) => CATALOGS.has(c)), FALLBACK]),
  ].filter((c) => c !== code);
};

// Shared with specs so they resolve languages exactly as the app does.
// `currentOnly` stops i18next from deriving its own chain from the code,
// which for a brand code skips the regional catalog; `fallbackFor` builds it.
export const i18nOptions = {
  fallbackLng: (code: string): string[] => fallbackFor(getFullLocale(), code),
  interpolation: {
    escapeValue: false,
    defaultVariables: cellTranslationVariables,
  },
  supportedLngs: SUPPORTED_LANGUAGES,
  nonExplicitSupportedLngs: true,
  load: "currentOnly",
  react: {
    useSuspense: true,
    transSupportBasicHtmlNodes: true,
    transKeepBasicHtmlNodesFor: ["br", "strong", "a"],
  },
  backend: {
    queryStringParams: { v: import.meta.env.commit_hash || "dev" },
  },
  returnNull: false,
} satisfies InitOptions;

export const initI18n = (): void => {
  if (isInitialized) {
    return;
  }
  isInitialized = true;

  void i18n
    .use(Backend)
    .use(initReactI18next)
    .init({ ...i18nOptions, lng: getFullLocale() });
  formatInLocale(i18n);
};

export const language2LetterCode = (): string => {
  const { language } = i18n;
  return language.slice(0, 2);
};

export { default as i18n } from "i18next";
